import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StorefrontIcon from "@mui/icons-material/Storefront";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { createApp } from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge-utils";
import config from "../../config";

const ShopifyOverview = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);

  // Initialize Shopify App Bridge
  const initializeAppBridge = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const host = params.get("host");

      if (!host) {
        throw new Error("Missing 'host' parameter from Shopify");
      }

      const app = createApp({
        apiKey: config.SHOPIFY_API_KEY,
        host: host,
      });

      return app;
    } catch (err) {
      console.error("Error initializing App Bridge:", err);
      throw err;
    }
  };

  // Get session token from Shopify
  const getToken = async () => {
    try {
      const app = initializeAppBridge();
      const token = await getSessionToken(app);
      setSessionToken(token);
      return token;
    } catch (err) {
      console.error("Error getting session token:", err);
      setError("Failed to authenticate with Shopify. Please refresh the page.");
      throw err;
    }
  };

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setLoading(true);

        // Check if we have host parameter (only present when loaded from Shopify Admin)
        const params = new URLSearchParams(window.location.search);
        const host = params.get("host");

        if (!host) {
          setError("This page must be accessed from Shopify Admin. Please install the app from Shopify App Store.");
          setLoading(false);
          return;
        }

        // Get session token from Shopify
        const token = await getToken();

        if (!token) {
          setError("Failed to get session token");
          return;
        }

        // Send token to backend for verification
        const response = await fetch(
          `${config.API_BASE_URL}/client/pos/shopify/overview`,
          {
            method: "GET",
            headers: {
              ...config.getHeaders(),
              "Authorization": `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (data.success) {
          setOverview(data.data);
          setError(null);
        } else {
          setError(data.message || "Failed to load store overview");
        }
      } catch (err) {
        console.error("Error fetching overview:", err);
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect this store?")) {
      return;
    }

    try {
      setDisconnecting(true);

      const response = await fetch(
        `${config.API_BASE_URL}/client/pos/shopify/disconnect`,
        {
          method: "POST",
          headers: {
            ...config.getHeaders(),
            "Authorization": `Bearer ${sessionToken}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setOverview({ ...overview, connection_status: "Disconnected", connected: false });
      } else {
        setError(data.message || "Failed to disconnect store");
      }
    } catch (err) {
      console.error("Error disconnecting:", err);
      setError("Failed to disconnect store");
    } finally {
      setDisconnecting(false);
    }
  };

  const openFullDashboard = () => {
    // Extract base URL from API_BASE_URL and navigate to dashboard
    const baseUrl = config.API_BASE_URL.replace("/api/v1", "").replace("http://", "https://");
    window.open(`${baseUrl}/#/`, "_blank");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !overview) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const isConnected = overview?.connected;

  return (
    <Box sx={{
      minHeight: "100vh",
      background: "#ffffff",
      p: 3
    }}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <StorefrontIcon sx={{ fontSize: 32, color: "#333" }} />
            <Typography variant="h4" sx={{ color: "#333", fontWeight: 600 }}>
              {overview?.store_name || "Shopify Store"}
            </Typography>
          </Box>
          <Typography sx={{ color: "#999", fontSize: "0.95rem" }}>
            {overview?.shop || "Store Overview"}
          </Typography>
        </Box>

        {/* Status Section */}
        <Box sx={{
          p: 3,
          mb: 4,
          border: "1px solid #e5e5e5",
          borderRadius: 2,
          background: isConnected ? "#f0fdf4" : "#fafafa"
        }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
            {isConnected ? (
              <>
                <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 24, mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333", mb: 0.5 }}>
                    Connected
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    Your store is connected and syncing
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <ErrorIcon sx={{ color: "#ef4444", fontSize: 24, mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333", mb: 0.5 }}>
                    Not Connected
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    Store is not currently connected
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, display: "block", mb: 0.5 }}>
                Domain
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                {overview?.domain_url || overview?.shop || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, display: "block", mb: 0.5 }}>
                Last Sync
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                {overview?.last_sync_date
                  ? new Date(overview.last_sync_date).toLocaleString()
                  : "—"}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Metrics Section */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{
              p: 3,
              border: "1px solid #e5e5e5",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2
            }}>
              <Box sx={{
                background: "#f3f4f6",
                p: 1.5,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <InventoryIcon sx={{ fontSize: 28, color: "#333" }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, display: "block" }}>
                  Products
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
                  {overview?.products_count || 0}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{
              p: 3,
              border: "1px solid #e5e5e5",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2
            }}>
              <Box sx={{
                background: "#f3f4f6",
                p: 1.5,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <ShoppingCartIcon sx={{ fontSize: 28, color: "#333" }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, display: "block" }}>
                  Orders
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
                  {overview?.orders_count || 0}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={openFullDashboard}
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: 180,
              py: 1.2,
              fontSize: "0.95rem",
              fontWeight: 600,
              background: "#333",
              color: "white",
              textTransform: "none",
              borderRadius: 1.5,
              "&:hover": {
                background: "#1a1a1a"
              }
            }}
          >
            Open Dashboard
          </Button>
          <Button
            variant="outlined"
            onClick={handleDisconnect}
            disabled={disconnecting || !isConnected}
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: 180,
              py: 1.2,
              fontSize: "0.95rem",
              fontWeight: 600,
              borderColor: "#e5e5e5",
              color: "#666",
              textTransform: "none",
              borderRadius: 1.5,
              "&:hover": {
                borderColor: "#999",
                backgroundColor: "#f9f9f9"
              },
              "&:disabled": {
                borderColor: "#e5e5e5",
                color: "#ccc"
              }
            }}
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ShopifyOverview;

